import React, { useCallback, useEffect, useState, useRef } from 'react'
import TablePanel from '../Support/TablePanel'
import Table from '@mui/material/Table'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TablePagination from '@mui/material/TablePagination'
import GetAppIcon from '@mui/icons-material/GetApp'
import Typography from '@mui/material/Typography'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import Grid from '@mui/material/Grid'
import LinkIcon from '@mui/icons-material/Link'
import Fade from '@mui/material/Fade'
import * as _ from 'underscore'
import Link from '@mui/material/Link'
import store from '../../redux/store'
import callAPI from '../../API/callSecureAPI'
import endpoints from '../../API/endpoints'
import { useAuth0 } from '../../contexts/auth0-context'
import download from 'downloadjs'
import { connect, useSelector } from 'react-redux'
import { setEvidenceTableNumberOfItems } from '../../redux/actions'
import { EnhancedTableHead, getComparator, stableSort, timeout } from '../../utilities/helpers'
import IconButton from '@mui/material/IconButton'
import { GlobalStyles } from '@mui/material'
import { EmptyPlaceholder } from '../../shared/components/EmptyPlaceholder'
import { arrayMove } from '@dnd-kit/sortable'
import { SortableTable } from '../../shared/components/SortableTable'
import useEventLogger from '../../shared/hooks/useEventLogger'
import { useGetEvidenceFile } from '../../shared/queries/useGetEvidenceFile'
import { useLocation, useHistory } from 'react-router-dom'
import { useInvalidateGetValueMessageLinkedDataQuery } from '../../shared/queries/useInvalidateGetValueMessageLinkedDataQuery'
import { useIsDarkMode } from '../../shared/hooks/useIsDarkMode'
import useInvalidateLinkingQueries from '../../shared/queries/linking/useInvalidateLinkingQueries'

function createData(citation, name, type, link, originalFileName, year, id, order) {
    return { citation, name, type, link, originalFileName, year, id, order }
}

function mapDispatchToProps(dispatch) {
    return {
        setEvidenceTableNumberOfRows: (num) => dispatch(setEvidenceTableNumberOfItems(num)),
    }
}

const DownloadEvidenceFileButton = ({ evidence }) => {
    const { refetch: getEvidenceFile } = useGetEvidenceFile(evidence.id)

    const handleDownloadEvidenceFile = async () => {
        const { data: evidenceFile } = await getEvidenceFile()
        await download(evidenceFile, evidence.originalFileName, evidenceFile.type)
    }

    return <GetAppIcon sx={{ color: 'figma.primary', cursor: 'pointer' }} onClick={handleDownloadEvidenceFile} />
}

function ConnectedEvidenceTable(props) {
    const { isAuthenticated, getTokenSilently } = useAuth0()
    const { logEvent, ACTIONS } = useEventLogger()
    const [page, setPage] = useState(0)
    const [dataRows, setDataRows] = useState([])
    const [loaded, setLoaded] = useState(false)
    const [forceRefresh, setForceRefresh] = useState(false)
    const itemsToDisplay = useSelector((state) => state.evidenceTableNumberOfItems || 10)
    const strategyId = useSelector((state) => state.strategy?._id)
    const [order, setOrder] = useState('asc')
    const [orderBy, setOrderBy] = useState('')
    const [evidenceData, setEvidenceData] = useState({})
    const [isPanelOpen, setIsPanelOpen] = useState(false)
    const [evidenceForPanel, setEvidenceForPanel] = useState({})
    const [updateTableFromPanel, setUpdateTableFromPanel] = useState(false)
    const [navigationStack, setNavigationStack] = useState([])
    //Needed for highlighting newly added evidence
    const [lastAddedId, setLastAddedId] = useState(null)
    const isDarkMode = useIsDarkMode()
    const [isLoading, setIsLoading] = useState(false)

    const location = useLocation()
    const history = useHistory()

    const { newFormData, refreshKey } = props
    // Columns declared as const weren't loading Store in time for render
    // eslint-disable-next-line no-unused-vars
    const [columns, setColumns] = useState([
        { id: 'index', label: '' },
        { id: 'citation', label: 'Citation' },
        { id: 'name', label: 'Title' },
        { id: 'type', label: 'Type', align: 'center', headPad: 2 },
        { id: 'link', label: 'Link', align: 'center', headPad: 2.5 },
        { id: 'originalFileName', label: 'File', align: 'center', headPad: 2.5 },
        { id: 'year', label: 'Year', align: 'center', headPad: 2.5 },
        { id: 'panel', label: '' },
    ])

    const { invalidateGetValueMessageLinkedDataQuery } = useInvalidateGetValueMessageLinkedDataQuery()
    const { invalidateLinkingQueries } = useInvalidateLinkingQueries()

    useEffect(() => {
        // This fires when component mounts and any time the VM list is modified
        if (forceRefresh) {
            setForceRefresh(false)
        }

        if (!isAuthenticated) {
            return
        }

        if (updateTableFromPanel) {
            invalidateGetValueMessageLinkedDataQuery()
            setUpdateTableFromPanel(false)
        }

        getEvidenceData()
        checkForAutoPanel()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [forceRefresh, isAuthenticated, updateTableFromPanel, refreshKey, strategyId])

    useEffect(() => {
        // track 'newFormData' from props for changes
        // if it changes, it means the Create Button is sending data
        if (!_.isEmpty(newFormData)) {
            newEvidence(newFormData).then(() => {
                setForceRefresh(true)
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newFormData])

    useEffect(() => {
        checkForAutoPanel()
    }, [location?.autoOpenData, location?.search, evidenceData])

    // Adjust page if dataRows length changes and current page no longer has items
    useEffect(() => {
        if (!dataRows.length) return

        const maxPage = Math.max(0, Math.ceil(dataRows.length / itemsToDisplay) - 1)

        // If the current page is out of range (e.g., after deleting last item of the page)
        if (page > maxPage) {
            setPage(maxPage) // move to previous valid page
        }
    }, [dataRows, page, itemsToDisplay])

    useEffect(() => {
        if (!lastAddedId || !dataRows.length) return

        const newIndex = dataRows.findIndex((row) => row.id === lastAddedId)
        if (newIndex === -1) return

        const newPage = Math.floor(newIndex / itemsToDisplay)
        const positionOnPage = newIndex % itemsToDisplay
        if (newPage !== page) setPage(newPage)

        const scrollWindowToBottom = () => {
            window.scrollTo({
                top: document.body.scrollHeight,
                behavior: 'smooth',
            })
        }

        // wait a short time for page render
        setTimeout(() => {
            if (positionOnPage > itemsToDisplay / 2) {
                scrollWindowToBottom()
            }
        }, 300)

        // clear highlight after 1.5s
        let timeoutId = setTimeout(() => setLastAddedId(null), 1500)

        return () => clearTimeout(timeoutId)
    }, [dataRows, lastAddedId, itemsToDisplay, page])

    async function getEvidenceData() {
        let token = await getTokenSilently({ audience: 'https://atlas.aesara.com' })
        let incomingEvidence = await callAPI(
            endpoints.getEvidenceForTable.url + strategyId,
            endpoints.getEvidenceForTable.method,
            token
        )

        if (incomingEvidence.success) {
            createDataRows(
                incomingEvidence.data.length > 0 ? incomingEvidence.data.sort((a, b) => a.order - b.order) : []
            )
        } else {
            createDataRows([])
            setLoaded(true)
            return
        }
        let evData = {}
        for (let ev of incomingEvidence.data) {
            evData[ev._id] = ev
        }
        setEvidenceData(evData)
        if (evidenceForPanel) {
            setEvidenceForPanel(evData[evidenceForPanel._id])
        }
    }

    async function newEvidence() {
        if (!setIsLoading) return // prevent double click on save button
        setIsLoading(true)

        try {
            let token = await getTokenSilently({ audience: 'https://atlas.aesara.com' })
            let response = await callAPI(
                endpoints.createEvidence.url,
                endpoints.createEvidence.method,
                token,
                newFormData
            )
            if (response.success) {
                invalidateLinkingQueries()

                if (response?.id) {
                    setLastAddedId(response.id)
                    setForceRefresh(true)
                }
                if (newFormData['s3Name']) {
                    callAPI(endpoints.processEvidenceForMaia.url, endpoints.processEvidenceForMaia.method, token, {
                        evidenceId: response.id,
                    })
                }
            }
        } catch (e) {
            console.error('❌ newEvidence error:', e)
        } finally {
            setIsLoading(false)
        }
    }

    async function createDataRows(data) {
        if (data && data.length) {
            let newArr = []
            for (const evidence of data) {
                let dataRow = createData(
                    evidence.shortCitation || evidence.citation,
                    evidence.name,
                    evidence.category,
                    evidence.link,
                    evidence.originalFileName,
                    evidence.year,
                    evidence._id,
                    evidence.order
                )
                newArr.push(dataRow)
            }
            newArr.sort((a, b) => a.order - b.order)
            setDataRows(newArr)
        }
        setLoaded(true)
    }

    const handleChangePage = (event, newPage) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (event) => {
        props.setEvidenceTableNumberOfRows(+event.target.value)
        setPage(0)
    }

    const handleSortDataRows = async (sortingEvent) => {
        const { active, over } = sortingEvent

        if (!over || active.id === over.id) {
            return
        }

        const indexOfDestinationRow = sortingEvent.over.data.current.sortable.index
        const indexOfSourceRow = sortingEvent.active.data.current.sortable.index
        const newDataRows = arrayMove(dataRows, indexOfSourceRow, indexOfDestinationRow)
        setDataRows(newDataRows)

        const token = await getTokenSilently({
            audience: 'https://atlas.aesara.com',
        })
        const ids = newDataRows.map((row) => row.id)
        await callAPI(endpoints.reorderEvidences.url, endpoints.reorderEvidences.method, token, ids)

        setOrderBy('index')
        setForceRefresh(true)
    }

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property)
    }

    const handlePanelOpen = useCallback(
        (id) => {
            if (evidenceData[id]) {
                logEvent(ACTIONS.VIEW, { type: 'Evidence', _id: id })
                setEvidenceForPanel(evidenceData[id])
                setIsPanelOpen(true)
            }
        },
        [evidenceData]
    )

    function handlePanelClose() {
        setEvidenceForPanel({})
        setNavigationStack([])
        setIsPanelOpen(false)
        location.autoOpenData = null
    }

    async function checkForAutoPanel() {
        if (location?.autoOpenData?.autoOpenData && location?.autoOpenData?.isAutoOpenPanel) {
            setNavigationStack(location.autoOpenData.previousDataStack ? location.autoOpenData.previousDataStack : [])
            await timeout(700)
            handlePanelOpen(location.autoOpenData.autoOpenData?._id)
            return
        }

        const evidenceId = new URLSearchParams(location?.search).get('id')
        if (evidenceId && evidenceData[evidenceId] && !isPanelOpen) {
            await timeout(700)
            handlePanelOpen(evidenceId)
            // Consume the deep-link param immediately so the panel doesn't re-trigger.
            history.replace(location.pathname)
            return
        }
    }

    const globalStyles = (
        <GlobalStyles
            styles={{
                '.MuiTableCell-root': {
                    borderBottom: 'none !important',
                },
            }}
        />
    )

    const sortedDataRows = stableSort(dataRows, getComparator(order, orderBy)).slice(
        page * itemsToDisplay,
        page * itemsToDisplay + itemsToDisplay
    )
    const isEvidenceDataEmpty = !sortedDataRows?.length

    return (
        <>
            {globalStyles}
            <Fade in={loaded} timeout={800}>
                <Grid container item xs>
                    {isEvidenceDataEmpty && (
                        <EmptyPlaceholder
                            title="No Evidence to display"
                            subtitle="Click 'Add New' on the left panel to create a new Evidence"
                        />
                    )}
                    {!isEvidenceDataEmpty && (
                        <>
                            <TableContainer>
                                <Table stickyHeader aria-label="sticky table">
                                    <EnhancedTableHead
                                        columns={columns}
                                        order={order}
                                        orderBy={orderBy}
                                        align={columns.align}
                                        onRequestSort={handleRequestSort}
                                    />
                                    <SortableTable.Body data={sortedDataRows} onSort={handleSortDataRows}>
                                        {({ rowData, index, rowProps }) => (
                                            <SortableTable.Row {...rowProps} highlighted={rowData.id === lastAddedId}>
                                                {columns.map((column) => {
                                                    const value = rowData[column.id]

                                                    if (column.id === 'originalFileName' && value && value.length > 0) {
                                                        return (
                                                            <TableCell
                                                                hover
                                                                key={column.id}
                                                                align={column.align}
                                                                sx={{ border: 'none' }}
                                                            >
                                                                <DownloadEvidenceFileButton evidence={rowData} />
                                                            </TableCell>
                                                        )
                                                    } else if (column.id === 'index') {
                                                        return (
                                                            <TableCell
                                                                hover
                                                                key={column.id}
                                                                align={column.align}
                                                                sx={{
                                                                    color: '#808080',
                                                                    border: 'none',
                                                                    borderTopLeftRadius: index === 0 ? 14 : 0,
                                                                    borderBottomLeftRadius:
                                                                        index ===
                                                                        dataRows.slice(
                                                                            page * itemsToDisplay,
                                                                            (page + 1) * itemsToDisplay
                                                                        ).length -
                                                                            1
                                                                            ? 14
                                                                            : 0,
                                                                }}
                                                            >
                                                                <Typography variant="body9">
                                                                    {index + page * itemsToDisplay + 1}.
                                                                </Typography>
                                                            </TableCell>
                                                        )
                                                    } else if (column.id === 'link' && value && value.length > 0) {
                                                        let formattedLink =
                                                            value.includes('http://') || value.includes('https://')
                                                                ? value
                                                                : 'http://' + value

                                                        return (
                                                            <TableCell
                                                                hover
                                                                key={column.id}
                                                                align={column.align}
                                                                sx={{ border: 'none' }}
                                                            >
                                                                <Link
                                                                    underline="none"
                                                                    href={formattedLink}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                >
                                                                    <LinkIcon />
                                                                </Link>
                                                            </TableCell>
                                                        )
                                                    } else if (column.id === 'panel') {
                                                        return (
                                                            <TableCell
                                                                key={column.id}
                                                                align={column.align}
                                                                sx={{
                                                                    border: 'none',
                                                                    borderTopRightRadius: index === 0 ? 14 : 0,
                                                                    borderBottomRightRadius:
                                                                        index ===
                                                                        dataRows.slice(
                                                                            page * itemsToDisplay,
                                                                            (page + 1) * itemsToDisplay
                                                                        ).length -
                                                                            1
                                                                            ? 14
                                                                            : 0,
                                                                    cursor: 'pointer',
                                                                }}
                                                                onClick={() => handlePanelOpen(rowData.id)}
                                                            >
                                                                <IconButton
                                                                    sx={(theme) => ({
                                                                        padding: theme.spacing(1.5),
                                                                        marginInline: theme.spacing(-0.75),
                                                                    })}
                                                                >
                                                                    <ArrowForwardIosIcon color="primary" />
                                                                </IconButton>
                                                            </TableCell>
                                                        )
                                                    } else {
                                                        return (
                                                            <TableCell
                                                                hover
                                                                key={column.id}
                                                                align={column.align}
                                                                sx={{ border: 'none' }}
                                                                style={{
                                                                    whiteSpace: 'break-spaces',
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant={
                                                                        column.format && typeof value === 'number'
                                                                            ? 'h4'
                                                                            : 'body1'
                                                                    }
                                                                    color={
                                                                        column.format && typeof value === 'number'
                                                                            ? 'primary'
                                                                            : ''
                                                                    }
                                                                >
                                                                    {column.format && typeof value === 'number'
                                                                        ? column.format(value)
                                                                        : value}
                                                                </Typography>
                                                            </TableCell>
                                                        )
                                                    }
                                                })}
                                            </SortableTable.Row>
                                        )}
                                    </SortableTable.Body>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                component="div"
                                count={dataRows.length}
                                rowsPerPage={itemsToDisplay}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </>
                    )}
                    {isPanelOpen && (
                        <TablePanel
                            title={evidenceForPanel?.name}
                            data={evidenceForPanel}
                            type={'evidence'}
                            closePanel={handlePanelClose}
                            openPanelAction={true}
                            updateParent={setUpdateTableFromPanel}
                            navigationStack={navigationStack}
                        />
                    )}
                </Grid>
            </Fade>
        </>
    )
}

const EvidenceTable = connect(null, mapDispatchToProps)(ConnectedEvidenceTable)

export default EvidenceTable
